/**
 * School Validation Schemas
 *
 * Zod validation schemas for school details update forms.
 * Uses OpenAPI-generated types to ensure schema matches backend structure.
 *
 * Pattern: Zod schema → react-hook-form → Direct API submission (no mapping!)
 *
 * Created: 2025-11-02
 * Version: 1.0
 */

import { z } from 'zod';
import type { SchoolDto } from '@/api/generated/types.gen';
import { addressSchema, phoneSchema } from '@/shared/validation/common.schemas';
import {
  saptamanaVerdeCampuri,
  ambeleSauNiciuna,
  startNuDupaSfarsit,
  intervalCompletatSauNecunoscut,
  MESAJ_AMBELE_SAU_NICIUNA,
  MESAJ_START_NU_DUPA_SFARSIT,
  MESAJ_INTERVAL_OBLIGATORIU,
} from '@/shared/validation/saptamana-verde';

/**
 * School Details Update Schema
 *
 * Validates editable fields for school details update form.
 * Field names match backend API (SchoolDto) exactly - NO mapping needed!
 *
 * Editable Fields:
 * - numeReprezentatLegal: Legal representative (school principal) -- deblocat 2026-09-21
 * - telefon: Phone number
 * - numarElevi: Number of students
 * - adresa: School address
 * - adresaLivrareAceeasiCuScoala: Same address for delivery?
 * - adresaLivrare: Delivery address (conditional)
 *
 * Read-Only Fields (not included in schema, rendered as disabled inputs):
 * - numeInstitutieInvatamant
 * - codSiiirUnitate
 * - cui
 *
 * Key Features:
 * - Uses backend field names (adresaLivrareAceeasiCuScoala)
 * - Nested address validation via addressSchema
 * - Conditional delivery address validation
 * - Direct API submission (form data → API without transformation)
 *
 * Usage:
 * ```ts
 * const form = useForm<SchoolDetailsFormData>({
 *   resolver: zodResolver(schoolDetailsSchema),
 * });
 * ```
 */
export const schoolDetailsSchema = z
  .object({
    // ===========================================================================
    // CONTACT INFORMATION (Editable)
    // ===========================================================================
    // Directorul (decizie owner 2026-09-21): se schimba de la un an la altul, iar protocolul se
    // completeaza din campul asta. `trim()` inainte de `min(1)`: un nume doar din spatii ar trece
    // altfel si ar ajunge in protocol. Mesajul e cel din `school-request.schemas.ts`.
    numeReprezentatLegal: z.string().trim().min(1, 'Numele reprezentantului legal este obligatoriu'),
    telefon: phoneSchema,
    // SE-29 (acelasi defect ca school-request.schemas.ts): `z.coerce.number()` pe `undefined`
    // (camp gol) da `NaN`, care pica pe `invalid_type_error`, NU pe `.min(1)` -- cazul real, cel
    // mai comun (camp necompletat) arata „trebuie sa fie un numar", mesajul scris pentru text
    // nenumeric, desi `<input type="number">` deja blocheaza asta din browser.
    numarElevi: z.coerce
      .number({ invalid_type_error: 'Numărul de elevi este obligatoriu' })
      .min(1, 'Numărul de elevi este obligatoriu')
      .int('Numărul de elevi trebuie să fie un număr întreg'),

    // ===========================================================================
    // SCHOOL ADDRESS (Editable)
    // ===========================================================================
    adresa: addressSchema,

    // ===========================================================================
    // DELIVERY ADDRESS (Conditional, Editable)
    // ===========================================================================
    adresaLivrareAceeasiCuScoala: z.boolean().default(true),
    adresaLivrare: addressSchema.optional(),

    // ===========================================================================
    // SAPTAMANA VERDE (Optional, Editable) -- decizie owner 2026-09-15: se completeaza
    // oricand din profilul scolii. Aceleasi campuri si reguli ca in cererea de inscriere.
    // ===========================================================================
    ...saptamanaVerdeCampuri,
  })
  .refine(
    (data) => {
      // If delivery address is different from school address,
      // then delivery address must be provided
      if (!data.adresaLivrareAceeasiCuScoala) {
        return !!data.adresaLivrare;
      }
      return true;
    },
    {
      // Frunza, nu obiectul (acelasi bug ca SE-07, `school-request.schemas.ts`):
      // `adresaLivrare` in sine n-are niciun input randat (campurile sunt
      // `adresaLivrare.judet`, `.oras` etc.), deci o eroare pe calea obiect nu
      // marcheaza niciun `aria-invalid` in DOM — `focusFirstInvalidField()` gasea 0
      // campuri si toastul cadea pe mesajul generic de rezerva (sau, aici, pe nimic
      // deloc, inainte de fallback-ul de mai jos).
      message: 'Adresa de livrare este obligatorie când este diferită de adresa școlii',
      path: ['adresaLivrare', 'judet'],
    }
  )
  // Tipul generat spune `Date` pentru interval; pe sarma e string ISO -- scos din constrangerea
  // `satisfies`, ca in `schoolRequestSchema`. (`satisfies` pe rand nou, dupa un comentariu, nu mai
  // se leaga de expresie: ASI inchide instructiunea inainte.)
  .refine(ambeleSauNiciuna, MESAJ_AMBELE_SAU_NICIUNA)
  .refine(startNuDupaSfarsit, MESAJ_START_NU_DUPA_SFARSIT)
  .refine(intervalCompletatSauNecunoscut, MESAJ_INTERVAL_OBLIGATORIU) satisfies z.ZodType<
    Partial<Omit<SchoolDto, 'saptamanaVerdeStart' | 'saptamanaVerdeEnd'>>
  >;

/**
 * TypeScript Type for School Details Form Data
 *
 * Inferred from Zod schema. Use this type for:
 * - Form hook return types
 * - Mutation function parameters
 * - Component props
 *
 * Example:
 * ```ts
 * const handleSubmit = async (data: SchoolDetailsFormData) => {
 *   await schoolsApi.update(data); // Direct submission!
 * };
 * ```
 */
export type SchoolDetailsFormData = z.output<typeof schoolDetailsSchema>;

/**
 * Valorile LIVE ale formularului. Difera de SchoolDetailsFormData pentru ca
 * `adresaLivrareAceeasiCuScoala` are `.default(true)`: optionala la intrare,
 * garantata la iesire.
 */
export type SchoolDetailsFormInput = z.input<typeof schoolDetailsSchema>;
